import { CrudServiceBase } from './CrudServiceBase';
import { FeeSheetDto } from '@/models/Fees/FeeSheetDto';
import { LoadOptions } from 'devextreme/data/load_options';
import { FeeSheetRowDto, FeeSheetRowTypeEnum, FeeSheetRowNotesDto } from '@/models/Fees/FeeSheetRowDto';
import { SendFeeSheetDto } from '@/models/Fees/SendFeeSheetDto';
import { FeeSheetMassAdderCreationDto } from '@/models/Fees/FeeSheetMassAdderDto';
import { HttpMethodEnum } from '@questelservices/common/dist/enums/HttpMethodEnum';
import { ExportFeeSheetsDto } from '@/models/Fees/ExportFeeSheetsDto';

export class FeeSheetsService extends CrudServiceBase<FeeSheetDto> {
  constructor(baseUrl: string) {
    super(baseUrl);
  }

  public async getFeeSheetPerClient(clientId: Guid): Promise<FeeSheetDto[]> {
    return await this.client.get(`/${clientId}`);
  }

  public async getClientFeeSheets(options: LoadOptions, clientId: Guid) : Promise<any> {
    return await this.client.getPostData(`/search/${clientId}/client`, options);
  }

  public async getAgentFeeSheets(options: LoadOptions, clientId: Guid) : Promise<any> {
    return await this.client.getPostData(`/search/${clientId}/agent`, options);
  }

  public async searchWithFilter(options: LoadOptions, clientId: Guid, forClient: boolean) : Promise<any> {
    return await this.client.getPostData(`/search/${clientId}?isForClient=${forClient}`, options);
  }

  public async searchFeeSheetDetailsPerFeeSheetId(feeSheetId: Guid, rowType?: FeeSheetRowTypeEnum): Promise<FeeSheetRowDto[]> {
    if (feeSheetId) {
      return await this.client.getPostData(`/search/fee-sheet-details/${feeSheetId}?type=${rowType || ''}`);
    }

    return await Promise.resolve([]);
  }

  public async getParentFeeSheetDetails(feeSheetId: Guid): Promise<FeeSheetRowDto[]> {
    if (feeSheetId) {
      return await this.client.get(`/search/fee-sheet-details/${feeSheetId}/parent`);
    }

    return await Promise.resolve([]);
  }

  public async insertFeeSheetRowAndRules(feeSheetRow: FeeSheetRowDto): Promise<any> {
    return await this.client.postJson(`/fee-sheet-row`, feeSheetRow);
  }

  public async updateFeeSheetRecord(feeSheet: FeeSheetDto): Promise<any> {
    return await this.client.sendRequest(HttpMethodEnum.Put, '', feeSheet);
  }

  public async deleteFeeSheet(feeSheetId: Guid): Promise<FeeSheetDto> {
    return await this.client.getData<FeeSheetDto>(HttpMethodEnum.Delete, `/${feeSheetId}`);
  }

  public async checkFeeSheetName(feeSheetName: string,client: string = '' , agent: string = ''): Promise<any> {
    return await this.client.get(`?feeSheetName=${feeSheetName}&clientId=${client}&agentId=${agent}`);
  }

  public async getAgentsWithParentFeeSheet(clientFeeSheetId: Guid) : Promise<Guid[]> {
    return await this.client.get(`/get-agents-by-parent-fee-sheet/${clientFeeSheetId}`);
  }

  public async getAgentNamesWithParentFeeSheet(clientFeeSheetId: Guid): Promise<string[]> {
    return await this.client.get(`/${clientFeeSheetId}/associated-agents`);
  }

  public async createAgentFeeSheetsFromList(clientFeeSheetId: Guid, massAdderDto: FeeSheetMassAdderCreationDto): Promise<any> {
    return await this.client.postJson(`/create-fee-sheets/${clientFeeSheetId}`, massAdderDto);
  }

  public async getFeeSheetsStatus(clientId: Guid, agentIds: Guid[]): Promise<FeeSheetDto[]> {
    return await this.client.getPostData(`/feeSheetStatus/${clientId}`, agentIds);
  }

  public async getExportFeeSheetsHTML(feeSheetIds: Guid[]): Promise<any> {
    return await this.client.getPostData(`/export-fee-sheets-html`, feeSheetIds);
  }

  public async getExportFeeSheetsPdf(payload: ExportFeeSheetsDto) {
    return await this.client.getPostData(`/export-fee-sheets`, payload);
  }

  public async updateFeeSheetRowNotes(feeSheetRowId: Guid, note: string | null) {
    let feeSheetRowNotes: FeeSheetRowNotesDto = { FeeSheetRowId: feeSheetRowId, Notes: note }
    return await this.client.getPostData(`/update-feesheet-row-notes`, feeSheetRowNotes);
  }

  public async sendFeesheets(sendFeeSheets:SendFeeSheetDto) {
    return await this.client.getPostData(`/send-feesheets`, sendFeeSheets);
  }

  public async getSentDates(feeSheetId: Guid) {
    return await this.client.get(`/sent-dates/${feeSheetId}`);
  }
}
